// @ts-nocheck
import { useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Badge,
  SimpleGrid,
  Avatar,
  Loader,
  Center,
} from '@mantine/core';
import { IconUsers, IconBuilding, IconClock } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { EmptyState } from '../../components/EmptyState';
import { useWhosIn } from '../../hooks/useTimeAttendance';
import { formatDuration, formatTime } from '../../utils/format';

interface WhosInEntry {
  employeeId: string;
  employeeName: string;
  punchInTime: string;
  branchName: string;
  departmentName: string;
}

export default function WhosInPage() {
  const { data, isLoading } = useWhosIn();
  const entries = (data ?? []) as WhosInEntry[];

  const groupedByDepartment = useMemo(() => {
    const groups: Record<string, WhosInEntry[]> = {};
    for (const entry of entries) {
      const dept = entry.departmentName || 'Unassigned';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(entry);
    }
    return groups;
  }, [entries]);

  const departments = Object.keys(groupedByDepartment).sort();

  const uniqueBranches = useMemo(() => {
    const branches = new Set(entries.map((e) => e.branchName).filter(Boolean));
    return branches.size;
  }, [entries]);

  return (
    <Stack gap="md">
      <PageHeader
        title="Who's In"
        subtitle="Employees currently clocked in (auto-refreshes every 30s)"
        breadcrumbs={[
          { label: 'Time & Attendance', to: '/admin/workforce/time' },
          { label: "Who's In" },
        ]}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard
          title="Clocked In"
          value={String(entries.length)}
          icon={<IconUsers size={24} />}
          color="green"
        />
        <StatCard
          title="Departments"
          value={String(departments.length)}
          icon={<IconBuilding size={24} />}
          color="blue"
        />
        <StatCard
          title="Branches"
          value={String(uniqueBranches)}
          icon={<IconBuilding size={24} />}
          color="teal"
        />
      </SimpleGrid>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<IconUsers size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No One Clocked In"
          message="No employees are currently clocked in."
        />
      ) : (
        departments.map((dept) => (
          <Paper key={dept} withBorder p="lg" radius="md">
            <Group mb="md" gap="sm">
              <IconBuilding size={18} />
              <Text fw={600} size="lg">
                {dept}
              </Text>
              <Badge size="sm" variant="light">
                {groupedByDepartment[dept]?.length ?? 0}
              </Badge>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
              {(groupedByDepartment[dept] ?? []).map((entry) => (
                <Paper
                  key={entry.employeeId}
                  withBorder
                  p="sm"
                  radius="sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                >
                  <Avatar size="md" radius="xl" color="blue">
                    {entry.employeeName
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate="end">
                      {entry.employeeName}
                    </Text>
                    <Group gap={4}>
                      <IconClock size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">
                        In at {formatTime(entry.punchInTime)}
                      </Text>
                    </Group>
                    {entry.branchName && (
                      <Text size="xs" c="dimmed">
                        {entry.branchName}
                      </Text>
                    )}
                  </div>
                  <Badge size="sm" color="green" variant="light">
                    {formatDuration(entry.punchInTime)}
                  </Badge>
                </Paper>
              ))}
            </SimpleGrid>
          </Paper>
        ))
      )}
    </Stack>
  );
}
